/**
 * @file dateUtils.js
 * @description Centralized date utilities for consistent date handling across the application.
 * 
 * This module provides timezone-aware date parsing, validation, and manipulation functions
 * to ensure consistent behavior across all components and tests.
 * 
 * Key principles:
 * - All date strings are parsed as local time (not UTC) to avoid timezone shifts
 * - Date validation is consistent across the application
 * - Date formatting follows standard patterns
 * - Functions are pure and predictable
 */

import moment from "moment-timezone";

// ============================================================================
// CONSTANTS
// ============================================================================

/**
 * Standard date format used throughout the application
 */
export const DATE_FORMAT = 'YYYY-MM-DD';

/**
 * ISO date regex pattern for validation
 */
export const DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Date range regex pattern (e.g., "2023-01-01...2023-01-31")
 * Allows optional whitespace around the dots
 */
export const DATE_RANGE_REGEX = /^\d{4}-\d{2}-\d{2}\s*\.\.\.\s*\d{4}-\d{2}-\d{2}$/;

/**
 * Day names in order (Sunday = 0, Monday = 1, etc.)
 */
export const DAY_NAMES = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

/**
 * Maximum chapters in the Bible (used for auto-calculating end dates)
 */
export const MAX_BIBLE_CHAPTERS = 1189;

// ============================================================================
// CORE DATE PARSING FUNCTIONS
// ============================================================================

/**
 * Parse a date string as local time (not UTC) to avoid timezone shifts.
 * 
 * This is the core function that should be used everywhere in the app for parsing
 * date strings. It ensures that "2023-04-24" is parsed as April 24th in the user's
 * local timezone, not as UTC which would cause timezone shifts.
 * 
 * @param {string|Date} dateInput - Date string in YYYY-MM-DD format or Date object
 * @returns {Date} Date object in local timezone
 * 
 * @example
 * // Instead of: new Date("2023-04-24") // ❌ Parses as UTC
 * // Use: parseLocalDate("2023-04-24")   // ✅ Parses as local time
 * 
 * const date = parseLocalDate("2023-04-24");
 * console.log(date.toDateString()); // "Mon Apr 24 2023" (in local timezone)
 */
export function parseLocalDate(dateInput) {
  if (!dateInput) {
    throw new Error('Date input is required');
  }
  
  if (dateInput instanceof Date) {
    return new Date(dateInput);
  }
  
  if (typeof dateInput === 'string') {
    // Add T00:00:00 to force local timezone parsing
    return new Date(dateInput + 'T00:00:00');
  }
  
  throw new Error(`Invalid date input type: ${typeof dateInput}`);
}

/**
 * Parse a date for testing purposes with explicit timezone handling.
 * This function is specifically designed for test files to ensure consistent
 * date parsing across different test environments.
 * 
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {Date} Date object in local timezone
 */
export function parseTestDate(dateString) {
  if (!dateString || typeof dateString !== 'string') {
    throw new Error('Test date must be a string in YYYY-MM-DD format');
  }
  
  if (!DATE_REGEX.test(dateString)) {
    throw new Error(`Invalid date format: ${dateString}. Expected YYYY-MM-DD`);
  }
  
  return parseLocalDate(dateString);
}

// ============================================================================
// DATE VALIDATION
// ============================================================================

/**
 * Check if a value is a valid Date object.
 * 
 * @param {any} date - Value to check
 * @returns {boolean} True if the value is a valid Date object
 */
export function isValidDate(date) {
  return date instanceof Date && !isNaN(date.getTime());
}

/**
 * Validate a date string format.
 * 
 * @param {string} dateString - Date string to validate
 * @returns {boolean} True if the date string is in valid YYYY-MM-DD format
 */
export function isValidDateString(dateString) {
  return typeof dateString === 'string' && DATE_REGEX.test(dateString);
}

/**
 * Validate and parse a date input, returning null if invalid.
 * 
 * @param {string|Date} dateInput - Date input to validate and parse
 * @returns {Date|null} Parsed date or null if invalid
 */
export function safeParseDate(dateInput) {
  try {
    const date = parseLocalDate(dateInput);
    return isValidDate(date) ? date : null;
  } catch {
    return null;
  }
}

/**
 * Validate that a date string represents a real date.
 * This checks that the parsed date matches the input string exactly,
 * preventing auto-correction of invalid dates (e.g., Feb 30 -> Mar 2).
 * 
 * @param {string} dateString - Date string in YYYY-MM-DD format
 * @returns {boolean} True if the date string represents a valid, real date
 */
export function isRealDate(dateString) {
  if (!isValidDateString(dateString)) {
    return false;
  }
  
  try {
    const date = parseLocalDate(dateString);
    if (!isValidDate(date)) {
      return false;
    }
    
    // Check that the parsed date matches the input string exactly
    const formattedDate = formatDateString(date);
    return formattedDate === dateString;
  } catch {
    return false;
  }
}

// ============================================================================
// DATE MANIPULATION
// ============================================================================

/**
 * Add days to a date and return a new Date object.
 * 
 * @param {Date} date - Base date
 * @param {number} days - Number of days to add (can be negative)
 * @returns {Date} New date with days added
 */
export function addDays(date, days) {
  if (!isValidDate(date)) {
    throw new Error('Invalid date provided');
  }
  
  const newDate = new Date(date);
  newDate.setUTCDate(newDate.getUTCDate() + days);
  return newDate;
}

/**
 * Normalize a date to the start of the day in local timezone.
 * This is useful for date comparisons where we only care about the date, not the time.
 * 
 * @param {Date} date - Date to normalize
 * @returns {Date} New date set to start of day (00:00:00)
 */
export function normalizeToStartOfDay(date) {
  if (!isValidDate(date)) {
    throw new Error('Invalid date provided');
  }
  
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/**
 * Check if two dates represent the same day (ignoring time).
 * 
 * @param {Date} date1 - First date
 * @param {Date} date2 - Second date
 * @returns {boolean} True if both dates are on the same day
 */
export function isSameDay(date1, date2) {
  if (!isValidDate(date1) || !isValidDate(date2)) {
    return false;
  }
  
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
}

/**
 * Check if two dates represent the same day (ignoring time).
 * 
 * @param {string} date1 - First date
 * @param {string} date2 - Second date
 * @returns {boolean} True if both dates are on the same day
 */
export function isSameDayString(date1, date2) {
  return date1 === date2;
}

// ============================================================================
// DATE FORMATTING
// ============================================================================

/**
 * Format a date as YYYY-MM-DD string.
 * 
 * @param {Date} date - Date to format
 * @returns {string} Date string in YYYY-MM-DD format
 */
export function formatDateString(date) {
  if (!isValidDate(date)) {
    throw new Error('Invalid date provided');
  }
  
  return date.toISOString().split('T')[0];
}

/**
 * Get the day name from a date.
 * 
 * @param {Date} date - Date to get day name from
 * @returns {string} Day name (e.g., 'Monday', 'Tuesday')
 */
export function getDayName(date) {
  if (!isValidDate(date)) {
    throw new Error('Invalid date provided');
  }
  
  return DAY_NAMES[date.getDay()];
}

/**
 * Check if a date falls on one of the specified days of the week.
 * 
 * @param {Date} date - Date to check
 * @param {string[]} daysOfWeek - Array of day names (e.g., ['Monday', 'Wednesday'])
 * @returns {boolean} True if the date falls on one of the specified days
 */
export function dateIsInDaysOfWeek(date, daysOfWeek) {
  if (!isValidDate(date)) {
    return false;
  }
  
  if (!Array.isArray(daysOfWeek)) {
    return false;
  }
  
  const dayName = getDayName(date);
  return daysOfWeek.includes(dayName);
}

// ============================================================================
// DATE LIST PARSING
// ============================================================================

/**
 * Parse a comma-separated list of dates and date ranges into date strings.
 * 
 * Supports formats:
 * - Single dates: "2023-04-24"
 * - Date ranges: "2023-04-24...2023-04-26"
 * - Mixed: "2023-04-24,2023-04-26...2023-04-28,2023-05-01"
 * 
 * @param {string} dateList - Comma-separated list of dates and ranges
 * @returns {string[]} Array of date strings in YYYY-MM-DD format, or empty array if invalid
 * 
 * @example
 * const dates = parseDateList("2023-04-24,2023-04-26...2023-04-28");
 * // Returns ["2023-04-24", "2023-04-26", "2023-04-27", "2023-04-28"]
 */
export function parseDateList(dateList) {
  const dates = [];
  
  if (!dateList || typeof dateList !== 'string') {
    return dates;
  }
  
  const dateStrings = dateList.split(',').map(s => s.trim()).filter(s => s);
  
  for (const dateString of dateStrings) {
    if (DATE_REGEX.test(dateString)) {
      // Single date
      if (isRealDate(dateString)) {
        dates.push(dateString);
      } else {
        // Return empty array on any parsing error for consistency
        return [];
      }
    } else if (DATE_RANGE_REGEX.test(dateString)) {
      // Date range - split on ... and trim whitespace
      const parts = dateString.split('...').map(s => s.trim());
      if (parts.length !== 2) {
        return [];
      }
      
      const [startDateStr, endDateStr] = parts;
      
      if (isRealDate(startDateStr) && isRealDate(endDateStr)) {
        try {
          const startDate = parseLocalDate(startDateStr);
          const endDate = parseLocalDate(endDateStr);
          
          if (startDate <= endDate) {
            const currentDate = new Date(startDate);
            while (currentDate <= endDate) {
              dates.push(formatDateString(currentDate));
              currentDate.setDate(currentDate.getDate() + 1);
            }
          } else {
            // Return empty array on any parsing error for consistency
            return [];
          }
        } catch {
          // Return empty array on any parsing error for consistency
          return [];
        }
      } else {
        // Return empty array on any parsing error for consistency
        return [];
      }
    } else {
      // Invalid format
      return [];
    }
  }
  
  return dates;
}

// ============================================================================
// CHALLENGE-SPECIFIC DATE UTILITIES
// ============================================================================

/**
 * Get all valid days between startDate and endDate (both inclusive) that fall on
 * specified days of the week and are not in the skip list.
 * 
 * @param {string} startDate - Start date (inclusive) in YYYY-MM-DD format
 * @param {string} endDate - End date (inclusive) in YYYY-MM-DD format, or empty string for auto-calculation
 * @param {string[]} daysOfWeek - Array of day names (e.g., ['Monday', 'Wednesday'])
 * @param {Date[]|string[]} datesToSkip - Array of dates to skip (can be Date objects or strings)
 * @returns {Date[]} Array of valid Date objects in local timezone
 */
export function getValidDays(startDate, endDate, daysOfWeek, datesToSkip = []) {
  const validDays = [];
  
  // Parse start date as local date
  const currentDateObj = parseLocalDate(startDate);
  
  // Parse or calculate end date
  let finalEndDateObj;
  if (endDate === '' || endDate === null || endDate === undefined) {
    // Auto-calculate end date for maximum possible chapters
    const daysNeeded = Math.ceil((MAX_BIBLE_CHAPTERS * 7) / daysOfWeek.length);
    finalEndDateObj = addDays(currentDateObj, daysNeeded + 7 + datesToSkip.length);
  } else {
    finalEndDateObj = parseLocalDate(endDate);
  }
  
  // Convert datesToSkip to normalized date strings for comparison
  const skipDateStrings = datesToSkip.map(skipDate => {
    if (skipDate instanceof Date) {
      return formatDateString(skipDate);
    } else if (typeof skipDate === 'string') {
      return skipDate;
    }
    return null;
  }).filter(Boolean);
  
  // Generate valid days
  let workingDateObj = new Date(currentDateObj);
  
  while (workingDateObj <= finalEndDateObj) {
    const workingDateString = formatDateString(workingDateObj);
    
    // Check if this date should be skipped
    const shouldSkip = skipDateStrings.includes(workingDateString);
    
    if (!shouldSkip && dateIsInDaysOfWeek(workingDateObj, daysOfWeek)) {
      validDays.push(new Date(workingDateObj));
    }
    
    // Move to next day
    workingDateObj.setDate(workingDateObj.getDate() + 1);
  }
  
  return validDays;
}

/**
 * Get today's date normalized to start of day in local timezone.
 * This is useful for finding the current day in challenge schedules.
 * 
 * @returns {Date} Today's date at 00:00:00 local time
 */
export function getTodayNormalized() {
  const now = new Date();
  return normalizeToStartOfDay(now);
}

// ============================================================================
// TIMEZONE AND TIME UTILITIES
// ============================================================================

/**
 * Compare two time strings of format "HH:MM", returning their difference in minutes.
 * 
 * @param {string} time1 - First time string (e.g., "14:30")
 * @param {string} time2 - Second time string (e.g., "09:15")
 * @returns {number} Difference in minutes (time1 - time2)
 */
export function compareTimes(time1, time2) {
  if (!time1 || !time2 || typeof time1 !== 'string' || typeof time2 !== 'string') {
    throw new Error('Both time parameters must be strings in HH:MM format');
  }
  
  const time1Split = time1.split(":");
  const time2Split = time2.split(":");
  
  if (time1Split.length !== 2 || time2Split.length !== 2) {
    throw new Error('Time strings must be in HH:MM format');
  }
  
  const time1Minutes = parseInt(time1Split[0]) * 60 + parseInt(time1Split[1]);
  const time2Minutes = parseInt(time2Split[0]) * 60 + parseInt(time2Split[1]);
  
  return time1Minutes - time2Minutes;
}

/**
 * Get the UTC time from a local hour and a timezone.
 * 
 * @param {number} localHour - An hour in the desired timezone (0-23)
 * @param {string} timezone - Timezone string (e.g., "America/New_York")
 * @returns {number[]} Array of [hours, minutes] in UTC
 */
export function getUTCTimeFromLocalHourAndTimezone(localHour, timezone) {
  if (typeof localHour !== 'number' || localHour < 0 || localHour > 23) {
    throw new Error('localHour must be a number between 0 and 23');
  }
  
  if (!timezone || typeof timezone !== 'string') {
    throw new Error('timezone must be a valid timezone string');
  }
  
  // Use moment.js for accurate timezone calculations (maintains compatibility)
  const now = moment();
  const localOffset = now.utcOffset();
  now.tz(timezone);
  const centralOffset = now.utcOffset();
  
  const diffInMinutes = (localOffset - centralOffset + 1440) % 1440;
  const hours = Math.floor(diffInMinutes / 60);
  const minutes = diffInMinutes % 60;
  
  const localDate = new Date();
  localDate.setHours(localHour + hours);
  localDate.setMinutes(minutes);
  localDate.setSeconds(0);
  localDate.setMilliseconds(0);
  
  return [localDate.getUTCHours(), localDate.getUTCMinutes()];
}

/**
 * Get a string in the format YYYY-MM-DD representing the current date in the local timezone.
 * 
 * @returns {string} Current date in YYYY-MM-DD format
 */
export function getCurrentLocalDateString() {
  const now = new Date();
  return formatDateString(now);
}

/**
 * Get a Date object representing the current date/time in local timezone.
 * 
 * @returns {Date} Current date/time
 */
export function getCurrentLocalDate() {
  return new Date();
}

/**
 * Get a Date object representing the current date/time with the given day offset.
 * 
 * @param {number} days - Number of days to offset (can be negative)
 * @returns {Date} Date with offset applied
 */
export function getCurrentLocalDateWithDayOffset(days) {
  const now = new Date();
  return addDays(now, days);
}

/**
 * Get the date formatted as YYYY-MM-DD for a certain timezone right now, plus an offset in days.
 * 
 * @param {string} timezone - Timezone string (e.g., "America/New_York")
 * @param {number} daysOffset - Number of days to offset (default: 0)
 * @returns {string} Formatted date string
 */
export function getFormattedDateForTimezone(timezone, daysOffset = 0) {
  if (!timezone || typeof timezone !== 'string') {
    throw new Error('timezone must be a valid timezone string');
  }
  
  const now = new Date();
  const offsetDate = addDays(now, daysOffset);
  
  // Get the date in the target timezone
  const dateInTimezone = new Date(offsetDate.toLocaleString('en-US', { timeZone: timezone }));
  
  return formatDateString(dateInTimezone);
}

/**
 * Get the date formatted as YYYY-MM-DD from a UTC date.
 * 
 * @param {Date} date - UTC date object
 * @returns {string} Formatted date string
 */
export function getFormattedDateFromUTC(date) {
  if (!isValidDate(date)) {
    throw new Error('Invalid date provided');
  }
  
  return date.toISOString().split('T')[0];
}

// ============================================================================
// LEGACY COMPATIBILITY
// ============================================================================

/**
 * Legacy function for backward compatibility.
 * @deprecated Use formatDateString instead
 */
export function stringifyDateLocally(date) {
  console.warn('stringifyDateLocally is deprecated. Use formatDateString instead.');
  return formatDateString(date);
}
